/**
 * 🔴 A WINDOW FOLLOWS WHAT THE COACH CAN SEE WHEN THE PAGE IS PINCH-ZOOMED (2026-10-18, William's still).
 *
 * His still showed the builder with its title, Save and ✕ above the top of the screen and its foot on screen — a shape a
 * centred `<dialog>` capped at `92vh` cannot take. MEASURED: it is a trackpad PINCH ZOOM (~1.3×, the still's left edge,
 * dock handle and foot all land within a few px of a 1.3× render). A pinch zooms the VISUAL viewport; a modal `<dialog>` is
 * `position: fixed`, which is laid out against the LAYOUT viewport — so the window kept its full-page size and place, and
 * the zoomed view showed only its lower part. Scrolling back up over the workout first scrolls the list to its top (and a
 * gesture latched to the list never reaches the page), so a coach mid-list could not get the ✕ back.
 *
 * The repair is one rule for every window (`app.css`, `html.ht-pinched dialog:modal`): while the page is zoomed, the
 * window's insets and caps are the VISIBLE rectangle's, so the head and the ✕ are always on screen. At scale 1 this
 * module writes nothing and every window is exactly what it was.
 */

/** The four insets (from the layout viewport's edges) and the size of what is visible, in CSS px; null = not zoomed. */
export type PinchedFrame = { top: number; right: number; bottom: number; left: number; width: number; height: number };

/** A scale this close to 1 is the browser's own rounding, not a pinch. */
const ZOOMED = 1.01;

export function pinchedFrame(
  vv: { scale: number; offsetTop: number; offsetLeft: number; width: number; height: number },
  layoutWidth: number, layoutHeight: number,
): PinchedFrame | null {
  if (!(vv.scale > ZOOMED)) return null;
  const top = Math.max(0, vv.offsetTop), left = Math.max(0, vv.offsetLeft);
  return {
    top, left, width: vv.width, height: vv.height,
    right: Math.max(0, layoutWidth - left - vv.width),
    bottom: Math.max(0, layoutHeight - top - vv.height),
  };
}

const VARS = ["top", "right", "bottom", "left", "width", "height"] as const;

/** Installed once, before the app mounts. Writes `--vv-*` and the `ht-pinched` class on `<html>` while zoomed. */
export function followPinchZoom(root: HTMLElement = document.documentElement): void {
  const vv = window.visualViewport;
  if (vv === null || vv === undefined) return;
  let queued = false;
  const apply = () => {
    queued = false;
    const f = pinchedFrame(vv, root.clientWidth, root.clientHeight);
    root.classList.toggle("ht-pinched", f !== null);
    for (const k of VARS) {
      if (f === null) root.style.removeProperty(`--vv-${k}`);
      else root.style.setProperty(`--vv-${k}`, `${f[k].toFixed(1)}px`);
    }
  };
  const schedule = () => { if (!queued) { queued = true; window.requestAnimationFrame(apply); } };
  vv.addEventListener("resize", schedule);
  vv.addEventListener("scroll", schedule);
  apply();
}
